import { computed, inject, signal } from '@angular/core';
import { ConfirmationService } from '@abp/ng.theme.shared';
import { SelectionType } from '@swimlane/ngx-datatable';
import { ABP, AbpWindowService, ListService, PagedResultDto } from '@abp/ng.core';
import type { GetTransactionsListResponse } from '@proxy/payments';
import { PaymentService } from '@proxy/payments';
import { GetRequestedItemsInput } from '@proxy/requested-items';
import { finalize, switchMap } from 'rxjs';

export abstract class AbstractPaymentHistoryViewService {
  protected readonly proxyService = inject(PaymentService);
  protected readonly confirmationService = inject(ConfirmationService);
  protected readonly list = inject(ListService);
  public readonly getList = this.proxyService.getTransactionsList;
  protected readonly abpWindowService = inject(AbpWindowService);

  readonly isDownloadInvoiceBusy = signal(false);

  // A signal, not a plain field: the ABP 10 layouts are OnPush, so a field set in a subscribe
  // is not redrawn until something else marks the layout dirty (#28).
  readonly data = signal<PagedResultDto<GetTransactionsListResponse>>({
    items: [],
    totalCount: 0,
  });
  selected = signal<GetTransactionsListResponse[]>([]);
  allSelected = signal(false);
  selectedCount = computed(() => this.selected().length);
  selectionType = SelectionType;
  filters = {} as GetRequestedItemsInput;

  protected clearAllSelection() {
    this.selected.set([]);
    this.allSelected.set(false);
  }

  hookToUserQuery(callback = () => {}, maxResultCount = 10, sorting = '') {
    const getData = (query: ABP.PageQueryParams) => {
      return this.proxyService.getTransactionsList({
        maxResultCount: query.maxResultCount,
        skipCount: query.skipCount,
        sorting: query.sorting
      });
    };

    const setData = (list: PagedResultDto<GetTransactionsListResponse>) => {
      this.data.set(list);
      callback();
      if (this.selectedCount() > 0) {
        this.clearAllSelection();
      }
    };

    this.list
      .hookToQuery(query =>
        getData({
          ...query,
          maxResultCount,
          sorting: sorting ? sorting : query?.sorting,
        })
      )
      .subscribe(setData);
  }

  downloadInvoice(transactionId:string) {
    this.isDownloadInvoiceBusy.set(true);
    this.proxyService
      .getDownloadToken()
      .pipe(
        switchMap(({ token }) =>
          this.proxyService.getIvoiceFile({
            downloadToken: token,
            transactionId,
            filterText: this.list.filter,
            ...this.filters,
          })
        ),
        finalize(() => this.isDownloadInvoiceBusy.set(false))
      )
      .subscribe({
        next: result => {
          this.abpWindowService.downloadBlob(result, 'Invoice.pdf');
        },
        error:(error)=>{
          this.confirmationService.error('::PaymentInvoiceDownloadFailed', '::PaymentInvoiceDownloadFailedTitle', { hideCancelBtn: true, yesText:'::ErrorConfirmation:ok' });
        }
      });
  }
}
